import { z } from "zod";
/** Default monthly subscription price (centimes) when no per-company override is set. */
export const DEFAULT_MONTHLY_PRICE_CENTIMES = 2000;
/** Default yearly subscription price (centimes) when no per-company override is set. */
export const DEFAULT_YEARLY_PRICE_CENTIMES = 20000;
/**
 * Resolve a company's subscription prices, falling back to defaults when an override is null.
 * Both override fields are linked (see SubscriptionPriceOverrideSchema) — callers that already
 * trust the persisted state can use this helper directly.
 *
 * Caller is expected to provide a real subscription setting (use findOneOrFail upstream).
 */
export function resolveSubscriptionPrices(setting) {
    return {
        monthlyCentimes: setting.monthlyPriceCentimes ?? DEFAULT_MONTHLY_PRICE_CENTIMES,
        yearlyCentimes: setting.yearlyPriceCentimes ?? DEFAULT_YEARLY_PRICE_CENTIMES,
    };
}
/**
 * True when at least one resolved cycle price differs from the default
 * (negotiated commercial price or beta-tester free plan).
 */
export function isCustomSubscriptionPrice(prices) {
    return (prices.monthlyCentimes !== DEFAULT_MONTHLY_PRICE_CENTIMES ||
        prices.yearlyCentimes !== DEFAULT_YEARLY_PRICE_CENTIMES);
}
export const SmsStatusPayloadSchema = z.object({
    unbilledCredits: z.number(),
    smsAvailable: z.boolean(),
    unitPrice: z.number().nullable(),
    estimatedAmount: z.number().nullable(),
    periodStart: z.string(),
    periodEnd: z.string(),
});
/**
 * Per-company subscription price override.
 *
 * `null` on both fields means the default price applies (20€ / 200€).
 * The two fields are linked: either both null, or both numbers.
 * A mixed state (one null, one number) is rejected.
 *
 * Used to support beta-testers (override = 0/0) and commercial deals
 * (override = negotiated price).
 */
export const SubscriptionPriceOverrideSchema = z
    .object({
    monthlyPriceCentimes: z.number().int().nonnegative().nullable(),
    yearlyPriceCentimes: z.number().int().nonnegative().nullable(),
})
    .refine((data) => (data.monthlyPriceCentimes === null && data.yearlyPriceCentimes === null) ||
    (data.monthlyPriceCentimes !== null && data.yearlyPriceCentimes !== null), {
    message: "monthlyPriceCentimes and yearlyPriceCentimes must both be set or both be null",
    path: ["yearlyPriceCentimes"],
});
//# sourceMappingURL=types.js.map